<template>
	<div class="select-page">
		<!-- Header -->
		<header class="select-header">
			<div class="select-header__brand">
				<div class="select-header__logo">
					<svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><polygon points="5 3 19 12 5 21 5 3"/></svg>
				</div>
				<div>
					<p class="select-header__name">{{ appName }}</p>
					<p class="select-header__sub">Workspace selection</p>
				</div>
			</div>
		</header>

		<!-- Content -->
		<div class="select-content">
			<div class="select-inner">
				<!-- Hero -->
				<div class="select-hero">
					<div class="select-hero__step">
						<span class="select-hero__step-line" />
						<span class="select-hero__step-text">Step 02 — Workspace</span>
					</div>
					<h1 class="select-hero__title">
						Choose a workspace<br/>to <span class="accent">continue.</span>
					</h1>
					<p class="select-hero__lead">
						You have access to {{ availableAgenciesList.length }} workspace{{ availableAgenciesList.length > 1 ? 's' : '' }}.
						Pick the one you want to work in — you can switch any time.
					</p>
				</div>

				<!-- Workspace list -->
				<div class="select-list">
					<div class="select-list__scroll">
						<button
							v-for="ag in availableAgenciesList"
							:key="`${ag.id}-${ag.role}`"
							type="button"
							class="select-row"
							:class="{ 'is-selected': selectedAgency && selectedAgency.id === ag.id && selectedAgency.role === ag.role }"
							@click="selectedAgency = ag"
						>
							<span class="select-radio"><span class="select-radio__dot" /></span>
							<span class="select-avatar" :style="avatarStyle(ag.name)">{{ initials(ag.name) }}</span>
							<span class="select-info">
								<span class="select-info__top">
									<span class="select-info__name">{{ ag.name }}</span>
									<span
										class="kind-badge"
										:class="ag.side === 'dsp' ? 'kind-badge--dsp' : 'kind-badge--ssp'"
									>{{ ag.side.toUpperCase() }}</span>
								</span>
								<span class="select-info__meta">
									<span>{{ formatRole(ag.role) }}</span>
								</span>
							</span>
						</button>
					</div>
				</div>

				<!-- Continue -->
				<div class="select-foot">
					<p v-if="selectedAgency" class="select-foot__label">
						Selected: <span class="name">{{ selectedAgency.name }}</span>
					</p>
					<span v-else />
					<button
						class="select-foot__btn"
						:disabled="!selectedAgency"
						@click="selectedAgency && switchAgencyAndRole(selectedAgency)"
					>
						Continue
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>


<script>
import ChooseAgency from '@/pages/choose-agency.vue';

export default {
	name: 'V2ChooseAgency',

	extends: ChooseAgency,

	computed: {
		appName() {
			return (this.$uioverrides && this.$uioverrides.name) || 'DISPL Ads';
		},
	},

	methods: {
		// Override parent's switchAgencyAndRole to redirect to V2 root
		switchAgencyAndRole(agency) {
			const hasNextRoute = this.$route.query && this.$route.query.next;

			this.$store.commit('app/setAgency', agency.id);
			this.$store.commit('app/setRole', agency.role);
			this.$store.commit('app/setSide', agency.side);

			window.location.href = hasNextRoute ? `/v2?next=${this.$route.query.next}` : '/v2';
		},

		initials(name) {
			if (!name) return '?';
			return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
		},

		avatarStyle(name) {
			const hue = name ? name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360 : 0;
			return {
				background: `hsl(${hue} 55% 88%)`,
				color: `hsl(${hue} 55% 32%)`,
			};
		},

		formatRole(role) {
			if (!role) return '';
			return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$teal: $v2-teal
$purple: $v2-purple
$text: $v2-text
$muted: $v2-text-muted
$border: $v2-border
$bg: $v2-bg
$card: $v2-card-bg
$font: $v2-font

.select-page
	min-height: 100vh
	display: flex
	flex-direction: column
	background: $bg
	font-family: $font

// ── Header ─────────────────────────
.select-header
	display: flex
	align-items: center
	justify-content: space-between
	padding: 20px 40px
	border-bottom: 1px solid rgba($border, 0.6)
	background: $card

.select-header__brand
	display: flex
	align-items: center
	gap: 12px

.select-header__logo
	height: 40px
	width: 40px
	border-radius: 16px
	background: $teal
	color: white
	display: flex
	align-items: center
	justify-content: center
	box-shadow: 0 8px 24px -6px rgba($teal, 0.5)

	svg
		width: 14px
		height: 14px

.select-header__name
	font-size: 14px
	font-weight: 700
	color: $text
	letter-spacing: -0.01em

.select-header__sub
	font-size: 10px
	font-weight: 500
	text-transform: uppercase
	letter-spacing: 0.16em
	color: $muted
	margin-top: 4px

// ── Content ────────────────────────
.select-content
	flex: 1
	display: flex
	align-items: center
	justify-content: center
	padding: 40px

.select-inner
	width: 100%
	max-width: 680px

// ── Hero ───────────────────────────
.select-hero
	margin-bottom: 32px

.select-hero__step
	display: inline-flex
	align-items: center
	gap: 12px
	margin-bottom: 16px

.select-hero__step-line
	width: 32px
	height: 1px
	background: $teal

.select-hero__step-text
	font-size: 11px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.22em
	color: $teal

.select-hero__title
	font-size: 40px
	font-weight: 700
	color: $text
	letter-spacing: -0.025em
	line-height: 1.05

	.accent
		color: $teal

.select-hero__lead
	margin-top: 16px
	font-size: 14px
	color: $muted
	max-width: 32rem
	line-height: 1.6

// ── List ───────────────────────────
.select-list
	background: $card
	border: 1px solid rgba($border, 0.6)
	border-radius: 16px
	overflow: hidden
	box-shadow: 0 1px 3px rgba(0,0,0,0.04)

.select-list__scroll
	max-height: 420px
	overflow-y: auto

.select-row
	width: 100%
	display: flex
	align-items: center
	gap: 16px
	padding: 16px 20px
	text-align: left
	background: transparent
	border: none
	border-bottom: 1px solid rgba($border, 0.5)
	cursor: pointer
	font-family: $font
	transition: background-color 0.15s

	&:last-child
		border-bottom: none

	&:hover
		background: rgba($bg, 0.5)

	&.is-selected
		background: rgba($teal, 0.04)

		&:hover
			background: rgba($teal, 0.05)

// ── Radio ──────────────────────────
.select-radio
	height: 20px
	width: 20px
	border-radius: 50%
	border: 2px solid $border
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0
	transition: all 0.18s ease

	.select-row.is-selected &
		border-color: $teal

.select-radio__dot
	height: 10px
	width: 10px
	border-radius: 50%
	background: $teal
	display: none

	.select-row.is-selected &
		display: block

// ── Avatar ─────────────────────────
.select-avatar
	height: 44px
	width: 44px
	border-radius: 12px
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0
	font-size: 13px
	font-weight: 700
	letter-spacing: -0.01em

// ── Info ───────────────────────────
.select-info
	flex: 1
	min-width: 0

.select-info__top
	display: flex
	align-items: center
	gap: 8px
	flex-wrap: wrap

.select-info__name
	font-size: 14px
	font-weight: 600
	color: $text
	white-space: nowrap
	overflow: hidden
	text-overflow: ellipsis

.select-info__meta
	display: flex
	align-items: center
	gap: 12px
	margin-top: 2px
	font-size: 11px
	color: $muted

// ── Kind badge ─────────────────────
.kind-badge
	display: inline-flex
	align-items: center
	gap: 4px
	padding: 2px 8px
	border-radius: 9999px
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.1em

	&--dsp
		background: rgba($teal, 0.10)
		color: $teal

	&--ssp
		background: rgba($purple, 0.12)
		color: $purple

// ── Footer ─────────────────────────
.select-foot
	margin-top: 24px
	display: flex
	align-items: center
	justify-content: space-between
	gap: 16px

.select-foot__label
	font-size: 11px
	font-weight: 500
	text-transform: uppercase
	letter-spacing: 0.14em
	color: $muted

	.name
		color: $text
		font-weight: 600
		text-transform: none
		letter-spacing: 0

.select-foot__btn
	height: 44px
	padding: 0 24px
	border-radius: 12px
	background: $teal
	color: white
	font-size: 14px
	font-weight: 600
	font-family: $font
	display: flex
	align-items: center
	gap: 8px
	border: none
	cursor: pointer
	box-shadow: 0 8px 24px -8px rgba($teal, 0.5)
	transition: all 0.2s ease

	&:hover
		transform: translateY(-2px)
		box-shadow: 0 12px 32px -8px rgba($teal, 0.6)

	&:disabled
		opacity: 0.5
		cursor: not-allowed
		transform: none

	svg
		width: 16px
		height: 16px
		transition: transform 0.2s

	&:hover svg
		transform: translateX(2px)
</style>
